const domain = (process.env.VUE_APP_IDENTITY_DOMAIN || "").replace(/\/$/, "");
const clientId = process.env.VUE_APP_ORATOR_IDENTITY_CLIENT_ID || "";
const storageKey = "orator-shared-sso-attempt";
const providerKey = "orator-shared-sso-provider";
const callbackPath = "/sso/callback";

function base64Url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomValue(length) {
  return base64Url(crypto.getRandomValues(new Uint8Array(length)));
}

function safeDestination(value) {
  return typeof value === "string" && value.startsWith("/")
    && !value.startsWith("//") && !value.startsWith(callbackPath)
    ? value : "/";
}

export function sharedSsoAvailable() {
  return domain.startsWith("https://") && Boolean(clientId);
}

function emailDomain(email) {
  const match = String(email || "").trim().toLowerCase().match(/^[^\s@]+@([a-z0-9-]+(?:\.[a-z0-9-]+)+)$/);
  if (!match) throw new Error("Enter a valid work email address.");
  return match[1];
}

export function connectionTestDomain(value) {
  return typeof value === "string" && /^test-[a-f0-9]{12}\.invalid$/.test(value) ? value : "";
}

export async function startSharedSignIn(destination = "/", options = {}) {
  if (!sharedSsoAvailable()) throw new Error("Shared sign-in is not configured.");
  const testDomain = connectionTestDomain(options.connectionTest);
  const provider = testDomain || (Object.prototype.hasOwnProperty.call(options, "email")
    ? emailDomain(options.email)
    : options.local ? "COGNITO" : sessionStorage.getItem(providerKey));
  const state = randomValue(16);
  const verifier = randomValue(32);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const redirectUri = `${window.location.origin}${callbackPath}`;
  sessionStorage.setItem(storageKey, JSON.stringify({
    state, verifier, redirectUri,
    destination: safeDestination(destination),
    startedAt: Date.now(), connectionTest: testDomain,
  }));
  const authorize = new URL(`${domain}/oauth2/authorize`);
  const parameters = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: "openid email aws.cognito.signin.user.admin",
    state,
    code_challenge_method: "S256",
    code_challenge: base64Url(new Uint8Array(digest)),
  });
  if (provider === "COGNITO") parameters.set("identity_provider", "COGNITO");
  else if (provider) parameters.set("idp_identifier", provider);
  authorize.search = parameters.toString();
  if (provider && !testDomain) sessionStorage.setItem(providerKey, provider);
  window.location.assign(authorize.toString());
}

export async function finishSharedSignIn() {
  if (!sharedSsoAvailable()) throw new Error("Shared sign-in is not configured.");
  const saved = sessionStorage.getItem(storageKey);
  sessionStorage.removeItem(storageKey);
  let attempt = null;
  try { attempt = saved ? JSON.parse(saved) : null; } catch (_) { /* Restart an invalid or expired attempt. */ }
  const callback = new URL(window.location.href);
  window.history.replaceState({}, "", callbackPath);
  if (
    !attempt || Date.now() - attempt.startedAt > 10 * 60 * 1000
    || callback.searchParams.get("state") !== attempt.state
    || !callback.searchParams.get("code")
  ) throw Object.assign(new Error("Your sign-in attempt expired or was interrupted. Please start again."), { code: "sso_retry", connectionTest: connectionTestDomain(attempt?.connectionTest) });
  const response = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code: callback.searchParams.get("code"),
      redirect_uri: attempt.redirectUri,
      code_verifier: attempt.verifier,
    }),
  });
  if (!response.ok) throw Object.assign(new Error("Sign-in could not be completed. Please try again."), { code: "sso_retry", connectionTest: connectionTestDomain(attempt?.connectionTest) });
  const tokens = await response.json();
  if (!tokens.access_token) throw new Error("Shared sign-in returned no access token.");
  return { accessToken: tokens.access_token, destination: safeDestination(attempt.destination), connectionTest: connectionTestDomain(attempt.connectionTest) };
}
